import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSettings, updateSettings, changePassword } from '../services/api';
import ErrorModal from '../components/ErrorModal';
import SuccessModal from '../components/SuccessModal';

const Settings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  
  const [settings, setSettings] = useState({
    summaryEmailEnabled: false,
    summaryEmailIntervalMinutes: 1440, // Default: 24 hours
    summaryEmailRecipients: [],
    lastSummarySentAt: null,
    summaryEmailOnlyWhenDown: false,
  });
  
  const [newEmail, setNewEmail] = useState('');
  const [countdown, setCountdown] = useState(null);
  
  // Password change state
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getSettings();
      // Handle migration from hours to minutes
      const intervalMinutes = response.data.summaryEmailIntervalMinutes || 
        (response.data.summaryEmailIntervalHours ? response.data.summaryEmailIntervalHours * 60 : 1440);
      
      setSettings({
        summaryEmailEnabled: response.data.summaryEmailEnabled || false,
        summaryEmailIntervalMinutes: intervalMinutes,
        summaryEmailRecipients: response.data.summaryEmailRecipients || [],
        lastSummarySentAt: response.data.lastSummarySentAt || null,
        summaryEmailOnlyWhenDown: response.data.summaryEmailOnlyWhenDown || false,
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load settings');
      setShowError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Countdown timer effect
  useEffect(() => {
    if (!settings.summaryEmailEnabled || settings.summaryEmailRecipients.length === 0) {
      setCountdown(null);
      return;
    }

    const calculateNextSendTime = () => {
      const now = Date.now();
      const intervalMs = settings.summaryEmailIntervalMinutes * 60 * 1000;
      
      if (settings.lastSummarySentAt) {
        const lastSent = new Date(settings.lastSummarySentAt).getTime();
        const nextSend = lastSent + intervalMs;
        return nextSend;
      } else {
        // If never sent, next send is in intervalMinutes from now
        return now + intervalMs;
      }
    };

    const updateCountdown = () => {
      const nextSendTime = calculateNextSendTime();
      const now = Date.now();
      const diff = nextSendTime - now;

      if (diff <= 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0, isOverdue: true });
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setCountdown({ days, hours, minutes, seconds, isOverdue: false });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [settings.summaryEmailEnabled, settings.summaryEmailIntervalMinutes, settings.lastSummarySentAt, settings.summaryEmailRecipients.length]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      // Validate email recipients if enabled
      if (settings.summaryEmailEnabled && settings.summaryEmailRecipients.length === 0) {
        setError('Please add at least one email recipient when summary emails are enabled');
        setShowError(true);
        setSaving(false);
        return;
      }

      // Validate interval
      if (settings.summaryEmailIntervalMinutes < 1 || settings.summaryEmailIntervalMinutes > 10080) {
        setError('Email interval must be between 1 minute and 10080 minutes (1 week)');
        setShowError(true);
        setSaving(false);
        return;
      }

      await updateSettings(settings);
      setSuccess('Settings saved successfully!');
      setShowSuccess(true);
      await loadSettings();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save settings');
      setShowError(true);
    } finally {
      setSaving(false);
    }
  };

  const handleAddEmail = () => {
    if (!newEmail.trim()) return;
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      setError('Please enter a valid email address');
      setShowError(true);
      return;
    }

    if (settings.summaryEmailRecipients.includes(newEmail.trim())) {
      setError('This email is already in the list');
      setShowError(true);
      return;
    }

    setSettings({
      ...settings,
      summaryEmailRecipients: [...settings.summaryEmailRecipients, newEmail.trim()],
    });
    setNewEmail('');
  };

  const handleRemoveEmail = (email) => {
    setSettings({
      ...settings,
      summaryEmailRecipients: settings.summaryEmailRecipients.filter(e => e !== email),
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleAddEmail();
    }
  };

  const handleChangePassword = async () => {
    try {
      setChangingPassword(true);
      setError(null);

      // Validation
      if (!oldPassword || !newPassword || !confirmPassword) {
        setError('All password fields are required');
        setShowError(true);
        setChangingPassword(false);
        return;
      }

      if (newPassword.length < 6) {
        setError('New password must be at least 6 characters long');
        setShowError(true);
        setChangingPassword(false);
        return;
      }

      if (newPassword !== confirmPassword) {
        setError('New passwords do not match');
        setShowError(true);
        setChangingPassword(false);
        return;
      }

      if (oldPassword === newPassword) {
        setError('New password must be different from current password');
        setShowError(true);
        setChangingPassword(false);
        return;
      }

      await changePassword({
        oldPassword,
        newPassword
      });

      setSuccess('Password changed successfully!');
      setShowSuccess(true);
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change password');
      setShowError(true);
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <button
          onClick={() => navigate('/')}
          className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mb-4 flex items-center"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Overview
        </button>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Settings</h1>
        <p className="text-gray-600 dark:text-gray-400">Configure summary email notifications</p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <svg className="w-6 h-6 mr-3 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Summary Email Settings</h2>
          </div>
          {settings.summaryEmailEnabled && settings.summaryEmailRecipients.length > 0 && countdown && (
            <div className={`rounded-lg px-4 py-3 border ${
              countdown.isOverdue 
                ? 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-700' 
                : 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-700'
            }`}>
              <div className="flex items-center gap-2">
                <svg className={`w-5 h-5 ${countdown.isOverdue ? 'text-red-600 dark:text-red-400' : 'text-blue-600 dark:text-blue-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <div className={`text-xs font-semibold uppercase tracking-wide ${
                    countdown.isOverdue 
                      ? 'text-red-600 dark:text-red-400' 
                      : 'text-blue-600 dark:text-blue-400'
                  }`}>
                    {countdown.isOverdue ? 'Email Overdue' : 'Next Email In'}
                  </div>
                  <div className={`text-lg font-bold ${
                    countdown.isOverdue 
                      ? 'text-red-700 dark:text-red-300' 
                      : 'text-blue-700 dark:text-blue-300'
                  }`}>
                    {countdown.isOverdue ? (
                      'Due Now'
                    ) : (
                      <>
                        {countdown.days > 0 && `${countdown.days}d `}
                        {countdown.hours > 0 && `${countdown.hours}h `}
                        {countdown.minutes > 0 && `${countdown.minutes}m `}
                        {countdown.seconds}s
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          {settings.summaryEmailEnabled && settings.summaryEmailRecipients.length === 0 && (
            <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-lg px-4 py-3">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div className="text-sm text-yellow-700 dark:text-yellow-300">
                  Add email recipients to enable countdown timer
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Current Settings Summary */}
        {settings.summaryEmailEnabled && (
          <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <div className="flex items-start">
              <svg className="w-5 h-5 mr-2 text-green-600 dark:text-green-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-green-900 dark:text-green-300 mb-2">Current Settings</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-green-700 dark:text-green-400 font-medium">Status:</span>
                    <span className="ml-2 text-green-900 dark:text-green-200">Enabled</span>
                  </div>
                  <div>
                    <span className="text-green-700 dark:text-green-400 font-medium">Interval:</span>
                    <span className="ml-2 text-green-900 dark:text-green-200">
                      {settings.summaryEmailIntervalMinutes >= 60 
                        ? `${(settings.summaryEmailIntervalMinutes / 60).toFixed(1)} hours`
                        : `${settings.summaryEmailIntervalMinutes} minutes`}
                    </span>
                  </div>
                  <div>
                    <span className="text-green-700 dark:text-green-400 font-medium">Recipients:</span>
                    <span className="ml-2 text-green-900 dark:text-green-200">{settings.summaryEmailRecipients.length} email(s)</span>
                  </div>
                  {settings.lastSummarySentAt && (
                    <div>
                      <span className="text-green-700 dark:text-green-400 font-medium">Last Sent:</span>
                      <span className="ml-2 text-green-900 dark:text-green-200">
                        {new Date(settings.lastSummarySentAt).toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-6">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
            <div>
              <label className="text-sm font-semibold text-gray-900 dark:text-white">
                Enable Summary Emails
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Send periodic summary reports of all monitor statuses
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.summaryEmailEnabled}
                onChange={(e) => setSettings({ ...settings, summaryEmailEnabled: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
            </label>
          </div>

          {/* Send Only When Down Toggle */}
          {settings.summaryEmailEnabled && (
            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
              <div>
                <label className="text-sm font-semibold text-gray-900 dark:text-white">
                  Send Only When Down
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Only send summary emails when at least one monitor is down
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.summaryEmailOnlyWhenDown}
                  onChange={(e) => setSettings({ ...settings, summaryEmailOnlyWhenDown: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
              </label>
            </div>
          )}

          {/* Email Interval */}
          {settings.summaryEmailEnabled && (
            <>
              <div>
                <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                  Email Interval (minutes)
                </label>
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <input
                      type="number"
                      min="1"
                      max="10080"
                      value={settings.summaryEmailIntervalMinutes}
                      onChange={(e) => setSettings({ ...settings, summaryEmailIntervalMinutes: parseInt(e.target.value) || 1440 })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setSettings({ ...settings, summaryEmailIntervalMinutes: 60 })}
                      className="px-3 py-2 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      title="1 hour"
                    >
                      1h
                    </button>
                    <button
                      type="button"
                      onClick={() => setSettings({ ...settings, summaryEmailIntervalMinutes: 1440 })}
                      className="px-3 py-2 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      title="24 hours"
                    >
                      24h
                    </button>
                    <button
                      type="button"
                      onClick={() => setSettings({ ...settings, summaryEmailIntervalMinutes: 10080 })}
                      className="px-3 py-2 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      title="1 week"
                    >
                      1w
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {settings.summaryEmailIntervalMinutes >= 60 
                    ? `${(settings.summaryEmailIntervalMinutes / 60).toFixed(1)} hours` 
                    : `${settings.summaryEmailIntervalMinutes} minutes`}
                  {settings.summaryEmailIntervalMinutes >= 1440 && ` (${(settings.summaryEmailIntervalMinutes / 1440).toFixed(1)} days)`}
                  {' '}• Range: 1 minute to 10080 minutes (1 week)
                </p>
              </div>

              {/* Email Recipients */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                  Email Recipients
                </label>
                <div className="flex gap-2 mb-3">
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Enter email address"
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <button
                    onClick={handleAddEmail}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                  >
                    Add
                  </button>
                </div>
                
                {settings.summaryEmailRecipients.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {settings.summaryEmailRecipients.map((email, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center px-3 py-2 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-lg text-sm font-medium border border-blue-200 dark:border-blue-700"
                      >
                        {email}
                        <button
                          onClick={() => handleRemoveEmail(email)}
                          className="ml-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                {settings.summaryEmailRecipients.length === 0 && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                    No recipients added. Add at least one email to receive summary reports.
                  </p>
                )}
              </div>
            </>
          )}

          {/* Save Summary Email Settings Button */}
          <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700 mt-6">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {saving ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Save Email Settings
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Password Change Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <div className="flex items-center mb-6">
          <svg className="w-6 h-6 mr-3 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
          </svg>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Change Password</h2>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Current Password
            </label>
            <div className="relative">
              <input
                type={showOldPassword ? 'text' : 'password'}
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                className="w-full px-4 py-3 pr-12 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                placeholder="Enter your current password"
              />
              <button
                type="button"
                onClick={() => setShowOldPassword(!showOldPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              >
                {showOldPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.29 3.29m13.42 13.42l-3.29-3.29M3 3l18 18" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              New Password
            </label>
            <div className="relative">
              <input
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-3 pr-12 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                placeholder="Enter your new password (min 6 characters)"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              >
                {showNewPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.29 3.29m13.42 13.42l-3.29-3.29M3 3l18 18" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Confirm New Password
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 pr-12 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                placeholder="Confirm your new password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              >
                {showConfirmPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.29 3.29m13.42 13.42l-3.29-3.29M3 3l18 18" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={handleChangePassword}
              disabled={changingPassword || !oldPassword || !newPassword || !confirmPassword}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {changingPassword ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Changing...
                </>
              ) : (
                'Change Password'
              )}
            </button>
          </div>
        </div>
      </div>

      <ErrorModal
        isOpen={showError}
        onClose={() => setShowError(false)}
        title="Error"
        message={error}
      />
      <SuccessModal
        isOpen={showSuccess}
        onClose={() => setShowSuccess(false)}
        title="Success"
        message={success}
      />
    </div>
  );
};

export default Settings;
